import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, Trash2, Upload, CalendarIcon, ImageIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIES = ["dog", "cat", "bird", "rabbit", "fish", "hamster", "other"];

function DateField({ value, onChange, placeholder, testId }) {
  const date = value ? parseISO(value) : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-left flex items-center gap-2 hover:border-[#FF8A65] transition-colors",
            !date && "text-slate-400"
          )}
        >
          <CalendarIcon size={14} className="text-slate-400" />
          {date ? format(date, "PP") : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date || undefined}
          onSelect={(d) => {
            if (!d) { onChange(""); return; }
            const iso = format(d, "yyyy-MM-dd");
            onChange(iso);
          }}
          captionLayout="dropdown"
          fromYear={1990}
          toYear={new Date().getFullYear() + 5}
        />
      </PopoverContent>
    </Popover>
  );
}

export default function AddPetModal({ open, onClose, onSaved, editPet }) {
  const { t } = useTranslation();
  const TABS = [t("addPet.tab_basic"), t("addPet.tab_vet"), t("addPet.tab_vaccinations")];

const EMPTY_FORM = {
  name: "", species: "dog", breed: "", date_of_birth: "", weight: "",
  color: "", microchip_id: "", vet_name: "", vet_phone: "", vet_address: "",
  medical_notes: "", photo_url: "", vaccinations: [], emergency_contacts: [],
};

  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setError("");
      setTab(0);
      if (editPet) {
        setForm({
          name: editPet.name || "",
          species: editPet.species || "dog",
          breed: editPet.breed || "",
          date_of_birth: editPet.date_of_birth || "",
          weight: editPet.weight || "",
          color: editPet.color || "",
          microchip_id: editPet.microchip_id || "",
          vet_name: editPet.vet_name || "",
          vet_phone: editPet.vet_phone || "",
          vet_address: editPet.vet_address || "",
          medical_notes: editPet.medical_notes || "",
          photo_url: editPet.photo_url || "",
          vaccinations: editPet.vaccinations || [],
          emergency_contacts: editPet.emergency_contacts || [],
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, editPet]);

  if (!open) return null;

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addVaccination = () =>
    setForm(f => ({ ...f, vaccinations: [...f.vaccinations, { name: "", date: "", next_due: "" }] }));

  const updateVaccination = (idx, key, val) =>
    setForm(f => {
      const v = [...f.vaccinations];
      v[idx] = { ...v[idx], [key]: val };
      return { ...f, vaccinations: v };
    });

  const removeVaccination = (idx) =>
    setForm(f => ({ ...f, vaccinations: f.vaccinations.filter((_, i) => i !== idx) }));

  const addContact = () =>
    setForm(f => ({ ...f, emergency_contacts: [...f.emergency_contacts, { name: "", relationship: "", phone: "" }] }));

  const updateContact = (idx, key, val) =>
    setForm(f => {
      const c = [...f.emergency_contacts];
      c[idx] = { ...c[idx], [key]: val };
      return { ...f, emergency_contacts: c };
    });

  const removeContact = (idx) =>
    setForm(f => ({ ...f, emergency_contacts: f.emergency_contacts.filter((_, i) => i !== idx) }));

  const handlePhotoPick = () => fileInputRef.current?.click();

  // Downscale any image to max 1600px wide (preserving aspect ratio) and
  // re-encode as JPEG q=0.85. Returns a File. This way modern phone-camera
  // photos (often 4-8 MB) become ~250-500 KB before they ever leave the
  // browser. Originals are never sent — we save bandwidth + GridFS storage
  // and the user never sees a "too large" error for ordinary photos.
  const resizeImage = (file) => new Promise((resolve, reject) => {
    const MAX_DIM = 1600;
    const QUALITY = 0.85;
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Could not encode image"));
            // GIFs lose animation when resized — keep tiny ones (< 1 MB) as-is.
            if (file.type === "image/gif" && file.size < 1024 * 1024) {
              return resolve(file);
            }
            resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
              type: "image/jpeg",
              lastModified: Date.now(),
            }));
          },
          "image/jpeg",
          QUALITY,
        );
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handlePhotoChange = async (e) => {
    const original = e.target.files?.[0];
    e.target.value = "";
    if (!original) return;
    // 5 MB upper bound — virtually no real photo will hit this after the
    // client-side resize, but we keep it as protection against pathological
    // inputs (e.g. 50 MB tiff).
    if (original.size > 5 * 1024 * 1024) {
      setError("Image must be under 5 MB. Try cropping or compressing it first.");
      return;
    }

    setError("");
    setUploading(true);
    let file = original;
    try {
      // Only attempt to resize standard browser-decodable image types.
      if (/^image\/(jpeg|png|webp|gif)$/.test(original.type)) {
        file = await resizeImage(original);
      }
    } catch (err) {
      // If resize fails, fall through and try the original — server will
      // reject it if truly too large, with a clear error message.
      // eslint-disable-next-line no-console
      console.warn("Photo resize failed, uploading original:", err);
      file = original;
    }

    // Preview instantly via data URL
    const reader = new FileReader();
    reader.onload = () => set("photo_url", reader.result);
    reader.readAsDataURL(file);

    // If we're editing an existing pet, persist immediately
    if (editPet?.pet_id) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/pets/${editPet.pet_id}/photo`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          setError(d.detail || t("addPet.photoUploadFailed"));
        } else {
          const data = await res.json();
          set("photo_url", data.photo_url);
        }
      } catch {
        setError(t("addPet.photoUploadNetwork"));
      } finally {
        setUploading(false);
      }
    } else {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError(t("addPet.nameRequired")); return; }
    setLoading(true);
    setError("");
    const body = {
      ...form,
      weight: form.weight ? parseFloat(form.weight) : null,
      vaccinations: form.vaccinations.filter(v => v.name.trim()),
      emergency_contacts: form.emergency_contacts.filter(c => c.name.trim() || c.phone.trim()),
    };
    const method = editPet ? "PUT" : "POST";
    const url = editPet ? `${API}/pets/${editPet.pet_id}` : `${API}/pets`;
    try {
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || t("addPet.saveFailed"));
        return;
      }
      // If we uploaded a photo for a brand-new pet (no editPet), persist it now
      const saved = await res.json();
      if (!editPet && form.photo_url?.startsWith("data:") && fileInputRef.current?.dataset?.pendingFileRef) {
        // currently no held file ref; handled on submit via body.photo_url being the data URL
      }
      onSaved(saved);
      onClose();
    } catch { setError(t("common.networkError")); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in-up font-['Figtree']">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-800 font-['Nunito']">{editPet ? t("addPet.editPet", { name: editPet.name }) : t("addPet.addNewPet")}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4">
          {TABS.map((tab_t, i) => (
            <button key={tab_t} onClick={() => setTab(i)} className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all ${tab === i ? "bg-[#FF8A65] text-white" : "text-slate-500 hover:bg-slate-100"}`}>{tab_t}</button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-xl">{error}</p>}

          {tab === 0 && (
            <>
              {/* Photo */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.photo")}</label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={handlePhotoPick}
                    data-testid="pet-photo-btn"
                    className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#FF8A65] hover:bg-[#FFF5E6] transition-colors flex items-center justify-center overflow-hidden bg-slate-50"
                  >
                    {form.photo_url ? (
                      <img src={form.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={22} className="text-slate-400" />
                    )}
                  </button>
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={handlePhotoPick}
                      disabled={uploading}
                      data-testid="pet-photo-upload-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors disabled:opacity-60"
                    >
                      <Upload size={13} /> {uploading ? t("passport.uploading") : form.photo_url ? t("addPet.changePhoto") : t("addPet.uploadPhoto")}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">{t("addPet.photoHint")}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handlePhotoChange}
                      className="hidden"
                      data-testid="pet-photo-file-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.petName")}</label>
                <input data-testid="pet-name-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder={t("addPet.petNamePlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.species")}</label>
                <Select value={form.species} onValueChange={(v) => set("species", v)}>
                  <SelectTrigger
                    data-testid="pet-species-select"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm h-auto focus:ring-2 focus:ring-[#FF8A65]/40"
                  >
                    <SelectValue placeholder={t("addPet.selectSpecies")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIES.map(s => (
                      <SelectItem key={s} value={s} data-testid={`species-option-${s}`}>
                        {t(`addPet.species_${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.breed")}</label>
                  <input value={form.breed} onChange={e => set("breed", e.target.value)} placeholder={t("addPet.breedPlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.color")}</label>
                  <input value={form.color} onChange={e => set("color", e.target.value)} placeholder={t("addPet.colorPlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.dateOfBirth")}</label>
                  <DateField value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} testId="pet-dob-picker" placeholder={t("addPet.pickDate")} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.weightKg")}</label>
                  <input type="number" step="0.1" value={form.weight} onChange={e => set("weight", e.target.value)} placeholder={t("addPet.weightPlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.medicalNotes")}</label>
                <textarea value={form.medical_notes} onChange={e => set("medical_notes", e.target.value)} placeholder={t("addPet.medicalNotesPlaceholder")} rows={3} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 resize-none" />
              </div>
            </>
          )}

          {tab === 1 && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.microchipId")}</label>
                <input value={form.microchip_id} onChange={e => set("microchip_id", e.target.value)} placeholder={t("addPet.microchipPlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.vetName")}</label>
                <input value={form.vet_name} onChange={e => set("vet_name", e.target.value)} placeholder={t("addPet.vetNamePlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.vetPhone")}</label>
                <input value={form.vet_phone} onChange={e => set("vet_phone", e.target.value)} placeholder={t("addPet.vetPhonePlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t("addPet.vetAddress")}</label>
                <input value={form.vet_address} onChange={e => set("vet_address", e.target.value)} placeholder={t("addPet.vetAddressPlaceholder")} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
              </div>

              {/* Emergency contacts */}
              <div className="pt-4 mt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-slate-600">{t("addPet.emergencyContacts")}</label>
                  <span className="text-[10px] text-slate-400">{t("addPet.emergencyContactsHint")}</span>
                </div>
                <div className="space-y-3">
                  {form.emergency_contacts.map((c, i) => (
                    <div key={`ec-${i}`} className="bg-slate-50 rounded-2xl p-3 relative border border-slate-100">
                      <button onClick={() => removeContact(i)} className="absolute top-2 right-2 p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-400 transition-colors" data-testid={`remove-contact-${i}`}><Trash2 size={13} /></button>
                      <div className="grid grid-cols-1 gap-2">
                        <input data-testid={`contact-name-${i}`} value={c.name} onChange={e => updateContact(i, "name", e.target.value)} placeholder={t("addPet.contactNamePlaceholder")} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid={`contact-rel-${i}`} value={c.relationship || ""} onChange={e => updateContact(i, "relationship", e.target.value)} placeholder={t("addPet.relationshipPlaceholder")} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                          <input data-testid={`contact-phone-${i}`} value={c.phone} onChange={e => updateContact(i, "phone", e.target.value)} placeholder={t("addPet.phonePlaceholder")} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button data-testid="add-contact-btn" onClick={addContact} type="button" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border-2 border-dashed border-[#FF8A65]/40 text-[#FF8A65] text-xs font-semibold hover:border-[#FF8A65] hover:bg-[#FFF5E6] transition-all">
                    <Plus size={14} /> {t("addPet.addEmergencyContact")}
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === 2 && (
            <div className="space-y-4">
              {form.vaccinations.map((v, i) => (
                <div key={`vacc-${i}`} className="bg-slate-50 rounded-2xl p-4 relative border border-slate-100">
                  <button onClick={() => removeVaccination(i)} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  <div className="grid grid-cols-1 gap-2">
                    <input value={v.name} onChange={e => updateVaccination(i, "name", e.target.value)} placeholder={t("addPet.vaccinePlaceholder")} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">{t("addPet.dateGiven")}</label>
                        <DateField value={v.date} onChange={(val) => updateVaccination(i, "date", val)} testId={`vacc-date-${i}`} placeholder={t("addPet.dateGiven")} />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">{t("addPet.nextDue")}</label>
                        <DateField value={v.next_due} onChange={(val) => updateVaccination(i, "next_due", val)} testId={`vacc-next-due-${i}`} placeholder={t("addPet.nextDue")} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button data-testid="add-vaccination-btn" onClick={addVaccination} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-[#FF8A65]/40 text-[#FF8A65] text-sm font-semibold hover:border-[#FF8A65] hover:bg-[#FFF5E6] transition-all">
                <Plus size={16} /> {t("addPet.addVaccination")}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-3 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">{t("common.cancel")}</button>
          <button data-testid="save-pet-btn" onClick={handleSubmit} disabled={loading} className="flex-1 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60">
            {loading ? t("common.saving") : editPet ? t("addPet.saveChanges") : t("addPet.addPetBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
